import Badge from '@codegouvfr/react-dsfr/Badge';
import Button from '@codegouvfr/react-dsfr/Button';
import Card from '@codegouvfr/react-dsfr/Card';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Tag from '@codegouvfr/react-dsfr/Tag';
import clsx from 'clsx';
import { Regions } from 'maestro-shared/referential/Region';
import { StageLabels } from 'maestro-shared/referential/Stage';
import {
  certificationIsRequired,
  regionIsRequired,
  type UserListItem
} from 'maestro-shared/schema/User/User';
import { UserRoleLabels } from 'maestro-shared/schema/User/UserRole';
import { isNotEmpty } from 'maestro-shared/utils/typescript';
import { type FunctionComponent, useContext } from 'react';
import { assert, type Equals } from 'tsafe';
import './UserCard.scss';

import { DepartmentLabels } from 'maestro-shared/referential/Department';
import { useMascarade } from '../../../components/Mascarade/useMascarade';
import { useAuthentication } from '../../../hooks/useAuthentication';
import { ApiClientContext } from '../../../services/apiClient';

type Props = {
  user: UserListItem;
  onEdit: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onToggleCertification: () => void;
};
export const UserCard: FunctionComponent<Props> = ({
  user,
  onEdit,
  onDisable,
  onEnable,
  onToggleCertification,
  ..._rest
}) => {
  assert<Equals<keyof typeof _rest, never>>();

  const { setMascaradeUserId } = useMascarade();
  const { hasAccountPermission } = useAuthentication();
  const apiClient = useContext(ApiClientContext);

  const { data: laboratories } = apiClient.useFindLaboratoriesQuery(
    {},
    { skip: !user.laboratoryId }
  );
  const laboratory = laboratories?.find(
    (laboratory) => laboratory.id === user.laboratoryId
  );

  const canCertify =
    hasAccountPermission('administrationMaestro') &&
    (certificationIsRequired(user) || !user.certified);

  return (
    <Card
      className={'user-list-item-card'}
      title={
        <div className={clsx('user-card-title')}>
          <div style={{ flexBasis: 'min-content' }}>
            {user.roles.map((role) => (
              <Tag as={'span'} small key={role}>
                {UserRoleLabels[role]}
              </Tag>
            ))}
          </div>
          <div className="d-flex-align-center">
            <Button
              size="small"
              className={clsx('fr-mr-1w')}
              onClick={onEdit}
              priority={'tertiary'}
              iconId={'fr-icon-edit-line'}
              title={'éditer'}
              data-testid={`user-edit-button-${user.id}`}
            />
            {!user.disabled ? (
              <>
                {canCertify && (
                  <Button
                    size="small"
                    className={clsx('fr-mr-1w')}
                    onClick={onToggleCertification}
                    priority={'tertiary'}
                    iconId={
                      user.certified
                        ? 'fr-icon-award-fill'
                        : 'fr-icon-award-line'
                    }
                    title={
                      user.certified
                        ? 'marquer comme non formé'
                        : 'marquer comme formé'
                    }
                    data-testid={`user-certification-button-${user.id}`}
                  />
                )}
                <Button
                  size="small"
                  onClick={onDisable}
                  priority={'tertiary'}
                  title={'désactiver'}
                  iconId={'fr-icon-logout-box-r-line'}
                  data-testid={`user-disable-button-${user.id}`}
                />
                {hasAccountPermission('administrationMaestro') && (
                  <Button
                    size="small"
                    onClick={() => setMascaradeUserId(user.id)}
                    priority={'tertiary'}
                    iconId={'fr-icon-group-line'}
                    className={'fr-ml-1w'}
                    title={`Usurper l'identité`}
                  />
                )}
              </>
            ) : (
              <Button
                size="small"
                onClick={onEnable}
                priority={'tertiary'}
                title={'activer'}
                iconId={'fr-icon-user-add-line'}
              />
            )}
          </div>
        </div>
      }
      titleAs={'h6'}
      desc={
        <span className={clsx('user-card-desc')}>
          {!user.certified && (
            <Badge severity="warning" small noIcon>
              Non formé
            </Badge>
          )}
          <span className={cx('fr-text--xl', 'fr-text--bold', 'fr-mb-0')}>
            {user.name ?? user.email}
          </span>
          {!!user.name && (
            <span className={cx('fr-text--sm', 'fr-text--light', 'fr-mb-0')}>
              {user.email}
            </span>
          )}
          <span className={clsx('user-card-region')}>
            <span
              className={cx('fr-icon-france-line', 'fr-icon--sm')}
              aria-hidden="true"
            />
            {regionIsRequired(user) && user.region
              ? `${Regions[user.region].name}${user.department ? ` - ${DepartmentLabels[user.department]}` : ''}`
              : 'France'}
          </span>
          {!!laboratory && (
            <span>
              <Tag as={'span'} small={true} className={clsx('fr-mb-1w')}>
                {laboratory.name}
              </Tag>
            </span>
          )}
          {isNotEmpty(user.stages) && (
            <span>
              {user.stages.map((stage) => (
                <Tag
                  key={stage}
                  as={'span'}
                  small={true}
                  className={clsx('fr-mb-1w')}
                >
                  {StageLabels[stage]}
                </Tag>
              ))}
            </span>
          )}
        </span>
      }
    />
  );
};
